/**
 * Question Data for the Onboarding Funnel
 * 
 * This file contains all question definitions for the multi-step funnel.
 * Questions are organized by their step number and include conditional logic
 * for branching based on user answers.
 * 
 * QUESTION FLOW:
 * 1. Intro Challenge (text area)
 * 2. Service Type Selection (branches to specific follow-ups)
 * 3. Service-specific Question 1 (conditional)
 * 4. Service-specific Question 2 (conditional)
 * 5. Micro Goals
 * 6. Security Concerns
 * 7. Final Goal (text area)
 * 8. Contact Information
 * 9. Thank You Page (not a question)
 */

import { Question, ServiceType } from '../types/funnel.types';

// ============================================
// MAIN QUESTIONS (Always Shown)
// ============================================

/**
 * Step 1: Introduction - Capture the main challenge
 * This open-ended question helps understand why they're here
 */
export const introQuestion: Question = {
  id: 'intro-challenge',
  step: 1,
  type: 'textarea',
  question: "What is a GOAL that you think that Ai Automation can help you achieve?",
  subtext: "Be specific - this helps us understand how AI can best help your business",
  placeholder: "Example: I want to automate appointment scheduling and customer inquiries so I can focus more on delivering great service...",
  validation: {
    required: true,
    minLength: 10,
  },
};

/**
 * Step 2: Service Type Selection
 * This determines which follow-up questions are shown (branching logic)
 */
export const serviceTypeQuestion: Question = {
  id: 'service-type',
  step: 2,
  type: 'single-select',
  question: "What type of service does your business provide?",
  subtext: "Select the category that best describes your primary business",
  options: [
    {
      id: 'home',
      label: 'Home Services',
      description: 'Plumbing, electrical, HVAC, landscaping',
      icon: '🏠',
    },
    {
      id: 'personal',
      label: 'Personal Services',
      description: 'Salons, fitness training, beauty, pet care',
      icon: '💇',
    },
    {
      id: 'professional',
      label: 'Professional Services',
      description: 'Consulting, accounting, legal, IT',
      icon: '💼',
    },
    {
      id: 'maintenance',
      label: 'Maintenance & Repair',
      description: 'Auto shops, appliance repair, equipment',
      icon: '🔧',
    },
  ],
};

// ============================================
// HOME SERVICES QUESTIONS (Conditional)
// ============================================

export const homeEmergencyQuestion: Question = {
  id: 'home-emergency',
  step: 3,
  type: 'single-select',
  question: "Do you offer emergency or 24/7 services?",
  subtext: "This helps us understand your availability needs",
  conditionalLogic: [{ field: 'service-type', equals: 'home' }],
  options: [
    { id: 'yes', label: 'Yes, we offer emergency services', icon: '🚨' },
    { id: 'no', label: 'No, standard business hours only', icon: '🕐' },
    { id: 'sometimes', label: 'Sometimes, for established clients', icon: '📞' },
  ],
};

export const homeSpecializationQuestion: Question = {
  id: 'home-specialization',
  step: 4,
  type: 'single-select',
  question: "Which area do you specialize in the most?",
  conditionalLogic: [{ field: 'service-type', equals: 'home' }],
  options: [
    { id: 'plumbing', label: 'Plumbing', icon: '🚰' },
    { id: 'electrical', label: 'Electrical', icon: '⚡' },
    { id: 'hvac', label: 'HVAC', icon: '❄️' },
    { id: 'landscaping', label: 'Landscaping', icon: '🌱' },
    { id: 'general', label: 'General Contracting', icon: '🔨' },
    { id: 'other', label: 'Other Home Service', icon: '🏗️' },
  ],
};

// ============================================
// PERSONAL SERVICES QUESTIONS (Conditional)
// ============================================

export const personalBookingQuestion: Question = {
  id: 'personal-booking',
  step: 3,
  type: 'single-select',
  question: "How do clients typically access your services?",
  subtext: "This helps us recommend the right booking solutions",
  conditionalLogic: [{ field: 'service-type', equals: 'personal' }],
  options: [
    { id: 'appointments', label: 'By appointment only', icon: '📅' },
    { id: 'walk-ins', label: 'Walk-ins welcome', icon: '🚶' },
    { id: 'both', label: 'Both appointments and walk-ins', icon: '✨' },
  ],
};

export const personalTypeQuestion: Question = {
  id: 'personal-type',
  step: 4,
  type: 'single-select',
  question: "What type of personal service best describes your business?",
  conditionalLogic: [{ field: 'service-type', equals: 'personal' }],
  options: [
    { id: 'beauty', label: 'Beauty & Cosmetics', icon: '💅' },
    { id: 'fitness', label: 'Fitness & Training', icon: '💪' },
    { id: 'wellness', label: 'Wellness & Spa', icon: '🧘' },
    { id: 'pet', label: 'Pet Services', icon: '🐕' },
    { id: 'education', label: 'Education & Tutoring', icon: '📚' },
    { id: 'other', label: 'Other Personal Service', icon: '✨' },
  ],
};

// ============================================
// PROFESSIONAL SERVICES QUESTIONS (Conditional)
// ============================================

export const professionalFieldQuestion: Question = {
  id: 'professional-field',
  step: 3,
  type: 'single-select',
  question: "Which professional field do you operate in?",
  conditionalLogic: [{ field: 'service-type', equals: 'professional' }],
  options: [
    { id: 'legal', label: 'Legal Services', icon: '⚖️' },
    { id: 'consulting', label: 'Business Consulting', icon: '📊' },
    { id: 'it', label: 'IT & Technology', icon: '💻' },
    { id: 'accounting', label: 'Accounting & Finance', icon: '📈' },
    { id: 'marketing', label: 'Marketing & PR', icon: '📱' },
    { id: 'other', label: 'Other Professional Service', icon: '🎯' },
  ],
};

export const professionalClienteleQuestion: Question = {
  id: 'professional-clientele',
  step: 4,
  type: 'single-select',
  question: "Who are your primary clients?",
  subtext: "Understanding your audience helps us tailor recommendations",
  conditionalLogic: [{ field: 'service-type', equals: 'professional' }],
  options: [
    { id: 'b2c', label: 'Individuals & Consumers', icon: '👤' },
    { id: 'b2b', label: 'Businesses & Organizations', icon: '🏢' },
    { id: 'both', label: 'Both individuals and businesses', icon: '🤝' },
  ],
};

// ============================================
// MAINTENANCE & REPAIR QUESTIONS (Conditional)
// ============================================

export const maintenanceFocusQuestion: Question = {
  id: 'maintenance-focus',
  step: 3,
  type: 'single-select',
  question: "What do you repair or maintain most often?",
  conditionalLogic: [{ field: 'service-type', equals: 'maintenance' }],
  options: [
    { id: 'vehicles', label: 'Vehicles & Automotive', icon: '🚗' },
    { id: 'appliances', label: 'Appliances', icon: '🔌' },
    { id: 'industrial', label: 'Industrial Equipment', icon: '⚙️' },
    { id: 'electronics', label: 'Electronics', icon: '📱' },
    { id: 'machinery', label: 'Heavy Machinery', icon: '🏗️' },
    { id: 'other', label: 'Other Repair/Maintenance', icon: '🔧' },
  ],
};

export const maintenanceFrequencyQuestion: Question = {
  id: 'maintenance-frequency',
  step: 4,
  type: 'single-select',
  question: "Is your service typically one-time or ongoing?",
  subtext: "This affects how we structure your automation recommendations",
  conditionalLogic: [{ field: 'service-type', equals: 'maintenance' }],
  options: [
    { id: 'one-time', label: 'Mostly one-time repairs', icon: '🔨' },
    { id: 'ongoing', label: 'Ongoing maintenance contracts', icon: '📋' },
    { id: 'both', label: 'Mix of both', icon: '🔄' },
  ],
};

// ============================================
// COMMON FOLLOW-UP QUESTIONS (Always Shown After Branch)
// ============================================

/**
 * Step 5: Micro Goals - Primary automation objective
 */
export const microGoalsQuestion: Question = {
  id: 'micro-goals',
  step: 5,
  type: 'single-select',
  question: "What's the primary goal you're hoping to achieve with AI?",
  subtext: "Choose the one that matters most to your business right now",
  options: [
    {
      id: 'scaling',
      label: 'Scale My Business',
      description: 'Serve more clients without hiring more staff',
      icon: '📈',
    },
    {
      id: 'efficiency',
      label: 'Improve Efficiency',
      description: 'Automate repetitive tasks and save time',
      icon: '⚡',
    },
    {
      id: 'customer-service',
      label: 'Better Customer Service',
      description: 'Respond faster and more consistently',
      icon: '💬',
    },
    {
      id: 'revenue',
      label: 'Increase Revenue',
      description: 'Capture more leads and close more deals',
      icon: '💰',
    },
    {
      id: 'other',
      label: 'Something Else',
      description: 'I have a different goal in mind',
      icon: '🎯',
    },
  ],
};

/**
 * Step 6: Security Concerns - Understanding their security mindset
 */
export const securityQuestion: Question = {
  id: 'security-concern',
  step: 6,
  type: 'single-select',
  question: "How concerned are you about your business's digital security?",
  subtext: "Understanding your security needs helps us recommend the right solutions",
  options: [
    {
      id: 'very',
      label: 'Very Concerned',
      description: 'Security is a top priority for my business',
      icon: '🔒',
    },
    {
      id: 'somewhat',
      label: 'Somewhat Concerned',
      description: "It matters, but not my biggest worry",
      icon: '🔐',
    },
    {
      id: 'not-much',
      label: 'Not Very Concerned',
      description: "Haven't thought about it much",
      icon: '🤔',
    },
  ],
};

/**
 * Step 7: Final Goal - Open-ended to capture detailed needs
 */
export const finalGoalQuestion: Question = {
  id: 'final-goal',
  step: 7,
  type: 'textarea',
  question: "In your own words, what's the biggest challenge where you think AI could help you the most?",
  subtext: "Be specific - this helps us create a customized solution for you",
  placeholder: "Example: I need help managing customer inquiries after hours so I don't miss potential jobs. Right now, if someone calls at 9pm, they probably go to a competitor...",
  validation: {
    required: true,
    minLength: 20,
  },
};

/**
 * Step 8: Contact Information - Lead capture
 */
export const contactInfoQuestion: Question = {
  id: 'contact-info',
  step: 8,
  type: 'multi-field',
  question: "Where should we send your personalized AI automation plan?",
  subtext: "We'll send you a detailed report based on your answers",
  fields: [
    {
      id: 'email',
      type: 'email',
      label: 'Email Address',
      placeholder: 'you@yourbusiness.com',
      validation: { required: true, email: true },
    },
    {
      id: 'name',
      type: 'text',
      label: 'First Name',
      placeholder: 'John',
      validation: { required: true, minLength: 2 },
    },
    {
      id: 'business-name',
      type: 'text',
      label: 'Business Name (Optional)',
      placeholder: 'Your Business LLC',
    },
  ],
};

// ============================================
// QUESTION ORGANIZATION
// ============================================

/**
 * All questions organized by service type
 * This makes it easy to look up which questions to show
 */
export const questionsByServiceType: Record<ServiceType, Question[]> = {
  home: [homeEmergencyQuestion, homeSpecializationQuestion],
  personal: [personalBookingQuestion, personalTypeQuestion],
  professional: [professionalFieldQuestion, professionalClienteleQuestion],
  maintenance: [maintenanceFocusQuestion, maintenanceFrequencyQuestion],
};

/**
 * Questions that are always shown (not conditional)
 */
export const baseQuestions: Question[] = [
  introQuestion,
  serviceTypeQuestion,
  // Steps 3-4 are conditional based on service type
  microGoalsQuestion,
  securityQuestion,
  finalGoalQuestion,
  contactInfoQuestion,
];

/**
 * Get the complete question flow based on selected service type
 * This is the main function to build the question sequence
 * 
 * @param serviceType - The selected service type (or undefined if not selected yet)
 * @returns Array of questions in the order they should be shown
 */
export const getQuestionFlow = (serviceType?: ServiceType): Question[] => {
  const flow: Question[] = [
    introQuestion,
    serviceTypeQuestion,
  ];

  // Add service-specific questions if a type is selected
  if (serviceType && questionsByServiceType[serviceType]) {
    flow.push(...questionsByServiceType[serviceType]);
  }

  // Add the rest of the common questions
  flow.push(
    microGoalsQuestion,
    securityQuestion,
    finalGoalQuestion,
    contactInfoQuestion
  );

  return flow;
};

/**
 * Total number of steps in the funnel
 * (including the thank you page which is step 9)
 */
export const TOTAL_STEPS = 9;

/**
 * Get a question by its ID
 * Useful for looking up question details when validating
 */
export const getQuestionById = (id: string): Question | undefined => {
  const allQuestions = [
    ...baseQuestions,
    ...Object.values(questionsByServiceType).flat(),
  ];
  return allQuestions.find(q => q.id === id);
};
